import {cn} from '@tempmee/core'
import {type HTMLAttributes, forwardRef} from 'react'

export type LinearProgressColor = 'primary' | 'success' | 'error'
export type LinearProgressVariant = 'determinate' | 'indeterminate' | 'buffer'
export type LinearProgressSize = 'small' | 'large'

export interface LinearProgressProps extends Omit<HTMLAttributes<HTMLDivElement>, 'color'> {
  /** Determinate fill, `0`–`100`. Omit (with no `variant`) for an indeterminate bar. */
  value?: number
  /**
   * Buffer fill (`0`–`100`) shown ahead of `value` as a lighter band, with dots
   * across the remainder. Only used when `variant="buffer"`.
   */
  valueBuffer?: number
  /**
   * `determinate` fills to `value`; `indeterminate` animates; `buffer` shows the
   * value fill + a buffer band + trailing dots. @default `value != null ? 'determinate' : 'indeterminate'`
   */
  variant?: LinearProgressVariant
  /** Track / fill color. Figma's Determinate/Indeterminate/Buffer = `primary`, Success = `success`, Fail = `error`. @default 'primary' */
  color?: LinearProgressColor
  /** Bar thickness — `small` (4px) or `large` (8px). @default 'small' */
  size?: LinearProgressSize
  /** Show the rounded `value%` to the right of the bar (determinate / buffer only). */
  label?: boolean
  /** Classes for the bar element itself (e.g. `w-[50px]` for the compact "Micro" table cell). */
  barClassName?: string
}

/** Full-color filled portion. */
const FILL: Record<LinearProgressColor, string> = {
  primary: 'bg-primary',
  success: 'bg-success',
  error: 'bg-error',
}

/** Lightened track (matches Figma's white-60%-over-color unfilled bar). */
const TRACK: Record<LinearProgressColor, string> = {
  primary: 'bg-primary/40',
  success: 'bg-success/40',
  error: 'bg-error/40',
}

/** `currentColor` source for the buffer dots — keeps them token-driven (no raw hex). */
const DOT_COLOR: Record<LinearProgressColor, string> = {
  primary: 'text-primary',
  success: 'text-success',
  error: 'text-error',
}

const BUFFER_DOTS = 'repeating-linear-gradient(to right, currentColor 0 4px, transparent 4px 8px)'

/**
 * Linear progress bar — the GoTu take on MUI `LinearProgress`. Pass **`value`**
 * (0–100) for a determinate bar, or omit it for an **indeterminate** animated bar;
 * `variant="buffer"` adds a buffer band + trailing dots. `size` is `small` (4px,
 * default) or `large` (8px), and `label` appends the `value%` on the right.
 *
 * The compact Figma "Micro" face (inside a table cell) is just this with a fixed
 * bar width: `<LinearProgress value={x} label barClassName="w-[50px]" />`.
 */
export const LinearProgress = forwardRef<HTMLDivElement, LinearProgressProps>(
  (
    {
      value,
      valueBuffer,
      variant,
      color = 'primary',
      size = 'small',
      label = false,
      className,
      barClassName,
      ...props
    },
    ref,
  ) => {
    const resolved: LinearProgressVariant =
      variant ?? (value != null ? 'determinate' : 'indeterminate')
    const isDeterminate = resolved === 'determinate' || resolved === 'buffer'
    const clamped = value != null ? Math.min(100, Math.max(0, value)) : 0
    const bufferClamped = Math.min(100, Math.max(clamped, valueBuffer ?? clamped))

    return (
      <div ref={ref} className={cn('flex w-full items-center gap-2', className)} {...props}>
        <div
          role="progressbar"
          tabIndex={-1}
          aria-valuenow={isDeterminate ? Math.round(clamped) : undefined}
          aria-valuemin={isDeterminate ? 0 : undefined}
          aria-valuemax={isDeterminate ? 100 : undefined}
          aria-label={isDeterminate ? undefined : 'Loading'}
          className={cn(
            'relative min-w-0 flex-1 overflow-hidden',
            size === 'large' ? 'h-2 rounded-full' : 'h-1 rounded-[16px]',
            TRACK[color],
            barClassName,
          )}
        >
          {/* buffer: dots across the un-filled remainder */}
          {resolved === 'buffer' && (
            <div
              className={cn('absolute inset-y-0 right-0', DOT_COLOR[color])}
              style={{left: `${clamped}%`, backgroundImage: BUFFER_DOTS}}
            />
          )}
          {/* buffer: lighter band between value and valueBuffer */}
          {resolved === 'buffer' && bufferClamped > clamped && (
            <div
              className={cn('absolute inset-y-0 rounded-full opacity-60', FILL[color])}
              style={{left: `${clamped}%`, width: `${bufferClamped - clamped}%`}}
            />
          )}
          {/* solid fill */}
          <div
            className={cn(
              'absolute inset-y-0 left-0 rounded-full',
              FILL[color],
              isDeterminate
                ? 'transition-[width] duration-300 ease-out'
                : 'w-2/5 animate-[linear-progress-indeterminate_1.4s_ease-in-out_infinite]',
            )}
            style={isDeterminate ? {width: `${clamped}%`} : undefined}
          />
        </div>
        {label && isDeterminate && (
          <span className="shrink-0 text-sm leading-[1.43] tracking-[0.17px] text-text-primary">
            {Math.round(clamped)}%
          </span>
        )}
      </div>
    )
  },
)
LinearProgress.displayName = 'LinearProgress'
